import type { CloudEdgeKind, ProofHop, ProofPath, ProofVia } from '@ai-secure-twin/shared';

/**
 * Lays proof paths out as a left-to-right graph (ADR 0009, UI direction B).
 *
 * Every path starts at the internet and the scan's own observation, then walks resources. Paths of
 * one finding share their beginning, so combining them merges the shared hops and fans out at the
 * first hop where they part — the canvas reads as one attack, not five. A hop's column is its
 * depth; within a column, nodes keep the order they first appear in, so the layout is the same on
 * every render.
 */

export const COLUMN_GAP = 196;
export const ROW_GAP = 92;
export const NODE_W = 156;
export const NODE_H = 58;
const PAD_X = 24;
const PAD_Y = 28;

export interface CanvasNode {
  key: string;
  col: number;
  row: number;
  x: number;
  y: number;
  hop: ProofHop;
  /** The end of at least one path drawn: a secret or data store, or the furthest proved hop. */
  target: boolean;
}

export interface CanvasEdge {
  from: string;
  to: string;
  /** How `to` was reached from `from`; absent from internet → observed → entry. */
  via?: ProofVia;
  kind: CloudEdgeKind | 'OBSERVED' | 'RESOLVES';
}

export interface Canvas {
  nodes: CanvasNode[];
  edges: CanvasEdge[];
  width: number;
  height: number;
  columns: number;
}

export const keyOf = (hop: ProofHop): string =>
  hop.kind === 'INTERNET' ? 'internet' : hop.kind === 'OBSERVED' ? 'observed' : hop.nodeId;

export function layoutCanvas(paths: readonly ProofPath[]): Canvas {
  const placed = new Map<string, { col: number; hop: ProofHop; target: boolean }>();
  const order: string[] = [];
  const edges = new Map<string, CanvasEdge>();

  for (const path of paths) {
    path.hops.forEach((hop, i) => {
      const key = keyOf(hop);
      const existing = placed.get(key);
      const isTarget = i === path.hops.length - 1 && hop.kind === 'RESOURCE';
      if (!existing) {
        placed.set(key, { col: i, hop, target: isTarget });
        order.push(key);
      } else {
        // Shortest paths place a node at one depth; keep the shallowest if they ever differ.
        existing.col = Math.min(existing.col, i);
        existing.target ||= isTarget;
      }
      if (i === 0) return;
      const from = keyOf(path.hops[i - 1]!);
      const id = `${from}>${key}`;
      if (edges.has(id)) return;
      edges.set(id, {
        from,
        to: key,
        ...(hop.kind === 'RESOURCE' && hop.via ? { via: hop.via } : {}),
        kind:
          hop.kind === 'OBSERVED'
            ? 'OBSERVED'
            : hop.kind === 'RESOURCE' && hop.via
              ? hop.via.edge
              : 'RESOLVES',
      });
    });
  }

  const byCol = new Map<number, string[]>();
  for (const key of order) {
    const col = placed.get(key)!.col;
    byCol.set(col, [...(byCol.get(col) ?? []), key]);
  }
  const columns = byCol.size === 0 ? 0 : Math.max(...byCol.keys()) + 1;
  const tallest = Math.max(1, ...[...byCol.values()].map((k) => k.length));
  const height = PAD_Y * 2 + (tallest - 1) * ROW_GAP + NODE_H;
  const width = PAD_X * 2 + Math.max(0, columns - 1) * COLUMN_GAP + NODE_W;

  const nodes: CanvasNode[] = [];
  for (const [col, keys] of byCol) {
    // Centre each column on the canvas, so a fan-out spreads around the line it leaves.
    const span = (keys.length - 1) * ROW_GAP;
    keys.forEach((key, row) => {
      const p = placed.get(key)!;
      nodes.push({
        key,
        col,
        row,
        x: PAD_X + col * COLUMN_GAP,
        y: height / 2 - span / 2 + row * ROW_GAP - NODE_H / 2,
        hop: p.hop,
        target: p.target,
      });
    });
  }
  nodes.sort((a, b) => a.col - b.col || a.row - b.row);
  return { nodes, edges: [...edges.values()], width, height, columns };
}

/** A smooth left-to-right connector from the right edge of one node to the left edge of the next. */
export function connector(from: CanvasNode, to: CanvasNode): string {
  const x1 = from.x + NODE_W;
  const y1 = from.y + NODE_H / 2;
  const x2 = to.x - 6;
  const y2 = to.y + NODE_H / 2;
  const mid = (x1 + x2) / 2;
  return `M${x1},${y1} C${mid},${y1} ${mid},${y2} ${x2},${y2}`;
}
